import { createAuth0Client } from '@auth0/auth0-spa-js';

let auth0Client = null;

// Initialize Auth0 client with your existing configuration
const initAuth0Client = async () => {
  if (!auth0Client) {
    auth0Client = await createAuth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: `${window.location.origin}/login/callback`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access'
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true
    });
  }
  return auth0Client;
};

/**
 * Get authentication token with automatic refresh
 * @returns {Promise<string|null>} Access token or null if authentication fails
 */
const getAuthToken = async () => {
  try {
    // Initialize Auth0 client if not already done
    const client = await initAuth0Client();
    
    // Check if user is authenticated first
    const isAuthenticated = await client.isAuthenticated();
    if (!isAuthenticated) {
      console.log('User not authenticated');
      return null;
    }

    // Get token silently - this automatically handles refresh if needed
    const token = await client.getTokenSilently({
      timeoutInSeconds: 60,
      cacheMode: 'on' // Use cache when possible, refresh when expired
    });
    
    return token;
    
  } catch (error) {
    console.error('Error getting auth token:', error);
    
    // Handle specific Auth0 refresh errors
    if (error.error === 'login_required' || 
        error.error === 'consent_required' ||
        error.error === 'interaction_required') {
      
      console.log('Re-authentication required');
      
      // Clear any stale tokens
      clearAuth0Cache();
      
      // Don't redirect here - let the calling code handle it
      // This prevents infinite redirects during API calls
      return null;
    }
    
    // For network errors or temporary issues, try fallback
    if (error.error === 'timeout' || 
        error.error === 'network_error' ||
        error.message?.includes('Failed to fetch')) {
      
      console.log('Network error, trying fallback...');
      return getFallbackToken();
    }
    
    // For other errors, return null
    return null;
  }
};

/**
 * Force a fresh token by clearing cache and getting new token
 * @returns {Promise<string|null>} Fresh access token or null
 */
const getAuthTokenFresh = async () => {
  try {
    const client = await initAuth0Client();
    
    const isAuthenticated = await client.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    // Force fresh token by bypassing cache
    const token = await client.getTokenSilently({
      timeoutInSeconds: 60,
      cacheMode: 'off' // Force refresh
    });
    
    return token;
    
  } catch (error) {
    console.error('Error getting fresh token:', error);
    return null;
  }
};

/**
 * Clear Auth0 cache to force fresh authentication
 */
const clearAuth0Cache = () => {
  try {
    // Clear all Auth0 cache entries from localStorage
    const auth0Keys = Object.keys(localStorage).filter(key => 
      key.startsWith('@@auth0spajs@@')
    );
    
    auth0Keys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cleared cache: ${key}`);
    });
    
    console.log('Auth0 cache cleared');
  } catch (error) {
    console.error('Error clearing Auth0 cache:', error);
  }
};

/**
 * Fallback method to get token from localStorage (legacy support)
 * @returns {string|null} Access token from localStorage or null
 */
const getFallbackToken = () => {
  try {
    const auth0TokenKey = `@@auth0spajs@@::${process.env.REACT_APP_AUTH0_CLIENT_ID}::${process.env.REACT_APP_AUTH0_AUDIENCE}::openid profile email offline_access`;
    const auth0TokenStorage = localStorage.getItem(auth0TokenKey);
    
    if (auth0TokenStorage) {
      const tokenObject = JSON.parse(auth0TokenStorage);
      
      if (tokenObject && tokenObject.body && tokenObject.body.access_token) {
        // Check if token has expiration info
        if (tokenObject.body.expires_in && tokenObject.body.decodedToken) {
          const expirationTime = tokenObject.body.decodedToken.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          
          if (currentTime >= expirationTime) {
            console.warn('Fallback token is expired');
            return null;
          }
        }
        
        console.log('Using fallback token from localStorage');
        return tokenObject.body.access_token;
      } else {
        console.error('Invalid token object in localStorage');
      }
    } else {
      console.log('No token found in localStorage');
    }
  } catch (error) {
    console.error('Error getting fallback token:', error);
  }
  
  return null;
};

/**
 * Check if user is currently authenticated
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 */
const isAuthenticated = async () => {
  try {
    const client = await initAuth0Client();
    return await client.isAuthenticated();
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

/**
 * Get user info if authenticated
 * @returns {Promise<object|null>} User object or null
 */
const getUser = async () => {
  try {
    const client = await initAuth0Client();
    const authenticated = await client.isAuthenticated();
    
    if (authenticated) {
      return await client.getUser();
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

/**
 * Trigger login redirect
 * This should typically be handled by your Layout/UI components
 * @param {string} returnTo - Optional URL to return to after login (defaults to current page)
 * @returns {Promise<void>}
 */
const loginWithRedirect = async (returnTo) => {
  try {
    const client = await initAuth0Client();
    // Store the return URL in sessionStorage so it persists through the redirect
    const returnPath = returnTo || window.location.pathname + window.location.search;
    sessionStorage.setItem('auth0_return_to', returnPath);

    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/login/callback`
      }
    });
  } catch (error) {
    console.error('Error during login redirect:', error);
    throw error;
  }
};

/**
 * Handle the redirect callback after login
 * @returns {Promise<void>}
 */
const handleRedirectCallback = async () => {
  try {
    const client = await initAuth0Client();
    await client.handleRedirectCallback();
    console.log('Redirect callback handled successfully');
  } catch (error) {
    console.error('Error handling redirect callback:', error);
    throw error;
  }
};

/**
 * Logout the user
 * @param {object} options - Logout options
 * @returns {Promise<void>}
 */
const logout = async (options = {}) => {
  try {
    const client = await initAuth0Client();
    
    // Clear cache before logout
    clearAuth0Cache();
    
    await client.logout({
      logoutParams: {
        returnTo: options.returnTo || window.location.origin
      }
    });
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  }
};

// Export the main function as default
export default getAuthToken;

// Export additional utilities
export {
  getAuthTokenFresh,
  clearAuth0Cache,
  getFallbackToken,
  isAuthenticated,
  getUser,
  loginWithRedirect,
  handleRedirectCallback,
  logout,
  initAuth0Client
};